import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Chip,
  Pagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Checkbox,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  WarningAmber as WarningAmberIcon,
  FileDownload as FileDownloadIcon,
  AddShoppingCart as AddShoppingCartIcon
} from '@mui/icons-material';
import type { Book, Category, BookListParams } from '../types';
import { bookService, categoryService } from '../services/bookService';
import { useCartStore } from '../stores/useCartStore';
import { useAppStore } from '../stores/useAppStore';
import { isStudent, isTeacher, isAdmin, canManageBooksFull } from '../utils/rbac';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';
import { ExportDialog } from './ExportDialog';
import { type ExportColumn } from '../services/exportService';
import { logger } from '../utils/logger';

interface BookListProps {
  onEdit: (book: Book) => void;
  onView: (book: Book) => void;
  onAdd: () => void;
  filterFromReports?: string | null;
  categoryIdFromCategories?: string | null;
  refreshTrigger?: number;
}

export const BookList: React.FC<BookListProps> = ({ onEdit, onView, onAdd, filterFromReports, categoryIdFromCategories, refreshTrigger }) => {
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const isStudentRole = isStudent(userRoles);
  const canManageFull = canManageBooksFull(userRoles);
  // NOTE: If user has both Librarian and Teacher roles, canManageFull takes priority
  // Only treat as "Teacher-only" if NOT canManageFull
  const isTeacherRole = isTeacher(userRoles) && !canManageFull;

  const [books, setBooks] = useState<Book[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>(''); // Backward compatibility
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]); // New: multiple categories
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookToDelete, setBookToDelete] = useState<Book | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectedBookIds, setSelectedBookIds] = useState<string[]>([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const { addItem, items: cartItems } = useCartStore();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  const isAdminRole = isAdmin(userRoles);
  const canExport = !isStudentRole; // All users except students can export

  // Load both books and categories together
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load books and categories in parallel
      // Nếu có filter theo danh mục (1 hoặc nhiều), luôn load nhiều sách hơn và filter trên frontend
      const hasCategoryFilter = selectedCategoryIds.length > 0;
      const shouldLoadAll = hasCategoryFilter;
      const [booksResult, categoriesResult] = await Promise.all([
        bookService.getBooks({
          page: shouldLoadAll ? 1 : page,
          pageSize: shouldLoadAll ? 10000 : pageSize, // Khi filter theo danh mục thì lấy nhiều bản ghi để tự phân trang sau
          searchTerm: debouncedSearchTerm || undefined,
          // Không gửi categoryId lên backend nữa, luôn filter danh mục trên frontend bằng categoryIds
          // categoryId: selectedCategoryIds.length === 1 ? selectedCategoryIds[0] : (selectedCategory || undefined),
          availabilityFilter: filterFromReports || undefined
        }),
        categoryService.getCategories({ page: 1, pageSize: 1000 }) // Get all categories for dropdown
      ]);

      // Filter books by multiple categories if needed
      let filteredBooks = booksResult.books;
      if (hasCategoryFilter) {
        filteredBooks = booksResult.books.filter(book => {
          const bookCategoryIds = book.categoryIds || (book.categoryId ? [book.categoryId] : []);
          // Sách thuộc nếu có ÍT NHẤT 1 danh mục trùng với filter (OR logic)
          return selectedCategoryIds.some(selectedId => bookCategoryIds.includes(selectedId));
        });
      }

      // Apply pagination if filtering multiple categories
      let paginatedBooks = filteredBooks;
      let finalTotalCount = filteredBooks.length;
      if (shouldLoadAll) {
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        paginatedBooks = filteredBooks.slice(startIndex, endIndex);
        finalTotalCount = filteredBooks.length;
      } else {
        finalTotalCount = booksResult.totalCount;
      }

      // Debug: Log books data to check categories
      logger.log('📚 Books loaded:', paginatedBooks.length, 'Total:', finalTotalCount);
      if (paginatedBooks.length > 0) {
        logger.log('📚 First book categories:', {
          id: paginatedBooks[0].id,
          title: paginatedBooks[0].title,
          category: paginatedBooks[0].category,
          categories: paginatedBooks[0].categories,
          categoryIds: paginatedBooks[0].categoryIds
        });
      }

      setBooks(paginatedBooks);
      setTotalCount(finalTotalCount);
      // Backend returns object with categories array
      const categoriesList = categoriesResult.categories || [];
      logger.log('📚 Categories loaded in BookList:', categoriesList.length, categoriesList);
      setCategories(categoriesList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearchTerm, selectedCategory, selectedCategoryIds, filterFromReports, refreshTrigger]);

  // Apply filter from Reports
  useEffect(() => {
    if (filterFromReports) {
      // Clear search term when applying availability filter
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }
  }, [filterFromReports]);

  // Apply category filter from Categories page
  useEffect(() => {
    if (categoryIdFromCategories) {
      setSelectedCategory(categoryIdFromCategories);
      setPage(1);
      // Clear search term when applying category filter
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }
  }, [categoryIdFromCategories]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800); // Wait 800ms after user stops typing - more time for Vietnamese input

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when search term or category filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, selectedCategoryIds]);

  // Load both books and categories together
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    // Don't reset page immediately, let debounce handle it
  };

  const handleCategoryChange = (event: any) => {
    setSelectedCategory(event.target.value);
    setPage(1);
  };

  const handleCategoryIdsChange = (event: any, newValue: Category[]) => {
    const categoryIds = newValue.map(cat => cat.id);
    setSelectedCategoryIds(categoryIds);
    // Also update selectedCategory for backward compatibility (use first selected)
    setSelectedCategory(categoryIds.length > 0 ? categoryIds[0] : '');
    setPage(1);
  };

  const handleCategoryChipClick = (categoryId: string) => {
    // If already selected, deselect it; otherwise, select only this one
    if (selectedCategoryIds.includes(categoryId)) {
      setSelectedCategoryIds([]);
      setSelectedCategory('');
    } else {
      setSelectedCategoryIds([categoryId]);
      setSelectedCategory(categoryId);
    }
    setPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleDeleteClick = (book: Book) => {
    setBookToDelete(book);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bookToDelete) return;

    const bookTitle = bookToDelete.title; // Save title before deletion

    try {
      await bookService.deleteBook(bookToDelete.id);
      setDeleteDialogOpen(false);
      setBookToDelete(null);
      showSuccess(`Đã xóa sách "${bookTitle}" thành công!`);
      // Reload data immediately
      await loadData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Có lỗi xảy ra khi xóa sách';
      setError(errorMessage);
      showError(errorMessage);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBookToDelete(null);
  };

  const handleBulkDeleteClick = () => {
    if (selectedBookIds.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedBookIds.length === 0) return;

    const count = selectedBookIds.length;
    const idsToDelete = [...selectedBookIds];

    try {
      await bookService.deleteMultipleBooks(idsToDelete);
      setBulkDeleteDialogOpen(false);
      setSelectedBookIds([]);
      showSuccess(`Đã xóa ${count} cuốn sách thành công!`);
      // Reload data immediately
      await loadData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Có lỗi xảy ra khi xóa sách';
      setError(errorMessage);
      showError(errorMessage);
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const handleSelectBook = (bookId: string) => {
    setSelectedBookIds(prev =>
      prev.includes(bookId)
        ? prev.filter(id => id !== bookId)
        : [...prev, bookId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBookIds.length === books.length) {
      setSelectedBookIds([]);
    } else {
      setSelectedBookIds(books.map(book => book.id));
    }
  };

  const handleAddToCart = () => {
    const selectedBooks = books.filter(book => selectedBookIds.includes(book.id));
    selectedBooks.forEach(book => {
      if (book.availableCopies > 0) {
        addItem(book);
      }
    });
    setSelectedBookIds([]);
  };

  const handleAddSelectedToCart = () => {
    handleAddToCart();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('vi-VN');
    } catch (error) {
      return '-';
    }
  };

  const getAvailabilityColor = (available: number, total: number) => {
    const percentage = (available / total) * 100;
    if (percentage === 0) return 'error';
    if (percentage < 30) return 'warning';
    return 'success';
  };

  const handleExportBooks = async (dateFrom: Date | null, dateTo: Date | null): Promise<Book[]> => {
    return await bookService.exportBooks(dateFrom, dateTo);
  };

  const bookExportColumns: ExportColumn[] = [
    { key: 'title', label: 'Tên sách', width: 30 },
    { key: 'author', label: 'Tác giả', width: 20 },
    { key: 'isbn', label: 'ISBN', width: 15 },
    { key: 'publisher', label: 'Nhà xuất bản', width: 20 },
    { key: 'publishYear', label: 'Năm xuất bản', width: 12 },
    { key: 'category', label: 'Danh mục', width: 20 },
    { key: 'totalCopies', label: 'Tổng số bản', width: 12 },
    { key: 'availableCopies', label: 'Số bản có sẵn', width: 15 },
    { key: 'dateCreated', label: 'Ngày tạo', width: 15 }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder="Tìm kiếm theo tên, tác giả, ISBN..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          <Autocomplete
            multiple
            options={categories || []}
            getOptionLabel={(option) => option.name}
            value={categories.filter(cat => selectedCategoryIds.includes(cat.id)) || []}
            onChange={handleCategoryIdsChange}
            filterSelectedOptions
            sx={{ minWidth: 250 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Danh mục"
                placeholder="Chọn danh mục (có thể chọn nhiều)"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option.id}
                  size="small"
                />
              ))
            }
          />
          {canExport && (
            <Tooltip title="Xuất Excel" arrow>
              <IconButton
                color="primary"
                onClick={() => setExportDialogOpen(true)}
                aria-label="Xuất Excel"
                size="small"
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!isStudentRole && !isTeacherRole && (
            <Tooltip title="Thêm sách mới" arrow>
              <IconButton
                color="primary"
                onClick={onAdd}
                aria-label="Thêm sách mới"
                size="small"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {selectedBookIds.length > 0 && (
            <>
              {isAdminRole && (
                <Tooltip title={`Xóa (${selectedBookIds.length})`} arrow>
                  <span>
                    <IconButton
                      color="error"
                      onClick={handleBulkDeleteClick}
                      aria-label="Xóa"
                      size="small"
                      disabled={selectedBookIds.length === 0}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title={`Thêm vào giỏ (${selectedBookIds.length})`} arrow>
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleAddSelectedToCart}
                    aria-label="Thêm vào giỏ"
                    size="small"
                    disabled={selectedBookIds.length === 0}
                  >
                    <AddShoppingCartIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Paper>

      {/* Total count display */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Tổng cộng: <strong>{totalCount}</strong> đầu sách
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ minHeight: 200, width: '100%' }}>
        <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '32%' }}>Tên sách</TableCell>
              <TableCell sx={{ width: '16%' }}>Tác giả</TableCell>
              <TableCell sx={{ width: '15%' }}>Danh mục</TableCell>
              <TableCell sx={{ width: '12%' }}>Trạng Thái</TableCell>
              <TableCell align="center" sx={{ width: '18%' }}>Thao tác</TableCell>
              <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                <Checkbox
                  indeterminate={selectedBookIds.length > 0 && selectedBookIds.length < books.length}
                  checked={books.length > 0 && selectedBookIds.length === books.length}
                  onChange={handleSelectAll}
                  size="small"
                  sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {books.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <Typography variant="h6" color="text.secondary">
                      {searchTerm || selectedCategoryIds.length > 0 ?
                        'Không tìm thấy sách nào phù hợp với điều kiện tìm kiếm' :
                        'Chưa có sách nào trong hệ thống'
                      }
                    </Typography>
                    {(searchTerm || selectedCategoryIds.length > 0) && (
                      <Typography variant="body2" color="text.secondary">
                        Thử thay đổi từ khóa tìm kiếm hoặc chọn danh mục khác
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              books.map((book) => (
                <TableRow key={book.id}>
                  <TableCell>
                    {book.title.length > 50 ? (
                      <Tooltip title={book.title} arrow>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          onClick={() => onView(book)}
                          sx={{
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {book.title.substring(0, 50)}...
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        onClick={() => onView(book)}
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {book.title}
                      </Typography>
                    )}
                    {book.publisher && (
                      <Typography variant="caption" color="text.secondary">
                        {book.publisher}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{book.author}</TableCell>
                  <TableCell>
                    {(() => {
                      if (book.categories && book.categories.length > 0) {
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {book.categories.map((category) => (
                              <Chip
                                key={category.id}
                                label={category.name}
                                size="small"
                                onClick={() => handleCategoryChipClick(category.id)}
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                              />
                            ))}
                          </Box>
                        );
                      } else if (book.category) {
                        return (
                          <Chip
                            label={book.category.name}
                            size="small"
                            onClick={() => handleCategoryChipClick(book.category!.id)}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                          />
                        );
                      } else {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Danh mục không khớp dữ liệu hiện có">
                              <WarningAmberIcon color="warning" fontSize="small" />
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary">
                              Chưa phân loại
                            </Typography>
                          </Box>
                        );
                      }
                    })()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${book.availableCopies}/${book.totalCopies} có sẵn`}
                      color={getAvailabilityColor(book.availableCopies, book.totalCopies)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(book)}
                      title="Chỉnh sửa"
                      disableFocusRipple
                      disableRipple
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(book)}
                      title="Xóa"
                      color="error"
                      disableFocusRipple
                      disableRipple
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                    <Checkbox
                      checked={selectedBookIds.includes(book.id)}
                      onChange={() => handleSelectBook(book.id)}
                      disabled={book.availableCopies <= 0}
                      size="small"
                      sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > 0 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa sách "{bookToDelete?.title}" không?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Hủy</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteDialogOpen} onClose={handleBulkDeleteCancel}>
        <DialogTitle>Xác nhận xóa nhiều sách</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa {selectedBookIds.length} cuốn sách đã chọn không?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Hành động này không thể hoàn tác. Các sách đang được mượn sẽ không thể xóa.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteCancel}>Hủy</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExport={handleExportBooks}
        columns={bookExportColumns}
        fileName="Danh_sach_sach"
        sheetName="Sách"
        title="Xuất danh sách sách ra Excel"
        description="Chọn khoảng thời gian để xuất dữ liệu sách. Dữ liệu sẽ được xuất theo ngày tạo."
        requireDateRange={true}
      />

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Box>
  );
};
